import React, { useState } from "react";
import { toast } from "sonner";
import { Send, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const CONSENT = "I agree that ComplaintAdvisor may share the information submitted in this enquiry with relevant vendors for the purpose of responding to my request.";
const BUDGETS = ["Under ₹5,000", "₹5,000–₹25,000", "₹25,000–₹1,00,000", "Over ₹1,00,000", "Not sure"];

export const EnquiryDialog = ({ trigger, categorySlug, subcategorySlug, vendorSlug, categoryName, subcategoryName, serviceDefault }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [f, setF] = useState({
    name: "", mobile: "", email: "", city: "", locality: "",
    service_required: serviceDefault || subcategoryName || "", description: "",
    preferred_date: "", preferred_contact: "phone", budget_range: "", consent: false,
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    if (!f.name.trim() || !f.mobile.trim() || !f.email.trim() || !f.description.trim())
      return toast.error("Name, mobile, email and requirement are required");
    if (!f.consent) return toast.error("Please accept the consent statement");
    setSubmitting(true);
    try {
      const utm = {};
      new URLSearchParams(window.location.search).forEach((v, k) => { if (k.startsWith("utm_")) utm[k] = v; });
      const res = await api.post("/discovery/enquiry", {
        ...f, category_slug: categorySlug || null, subcategory_slug: subcategorySlug || null,
        vendor_slug: vendorSlug || null, source_url: window.location.href,
        utm: Object.keys(utm).length ? utm : null,
      });
      setDone(res.data);
      toast.success(`Enquiry ${res.data.enquiry_id} submitted`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit enquiry"); }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setDone(null); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="enquiry-dialog">
        <DialogHeader><DialogTitle>Submit an Enquiry{categoryName ? ` · ${categoryName}` : ""}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">We'll route your requirement to relevant providers. Outcomes are not guaranteed.</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="py-4 text-center space-y-2" data-testid="enquiry-success">
            <ShieldCheck className="h-10 w-10 text-emerald-600 mx-auto" />
            <p className="font-semibold text-slate-900">Your enquiry has been received</p>
            <p className="text-sm text-slate-500">Enquiry ID: <b className="font-mono">{done.enquiry_id}</b></p>
            <p className="text-xs text-slate-400">A confirmation email is on its way. We do not guarantee a quotation, appointment or outcome.</p>
            <Button className="mt-2 bg-teal-brand text-white" onClick={() => setOpen(false)}>Done</Button>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Name *</Label><Input value={f.name} onChange={set("name")} data-testid="enq-name" /></div>
              <div><Label>Mobile *</Label><Input value={f.mobile} onChange={set("mobile")} placeholder="+91…" data-testid="enq-mobile" /></div>
              <div><Label>Email *</Label><Input value={f.email} onChange={set("email")} placeholder="you@example.com" data-testid="enq-email" /></div>
              <div><Label>City</Label><Input value={f.city} onChange={set("city")} data-testid="enq-city" /></div>
              <div><Label>Locality</Label><Input value={f.locality} onChange={set("locality")} data-testid="enq-locality" /></div>
              <div><Label>Required service</Label><Input value={f.service_required} onChange={set("service_required")} data-testid="enq-service" /></div>
              <div><Label>Preferred date</Label><Input type="date" value={f.preferred_date} onChange={set("preferred_date")} data-testid="enq-date" /></div>
              <div>
                <Label>Preferred contact</Label>
                <Select value={f.preferred_contact} onValueChange={(v) => setF({ ...f, preferred_contact: v })}>
                  <SelectTrigger data-testid="enq-contact"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="phone">Phone call</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label>Budget range (optional)</Label>
                <Select value={f.budget_range} onValueChange={(v) => setF({ ...f, budget_range: v })}>
                  <SelectTrigger data-testid="enq-budget"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{BUDGETS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2"><Label>Describe your requirement *</Label><Textarea rows={3} value={f.description} onChange={set("description")} data-testid="enq-description" /></div>
            </div>
            <label className="flex items-start gap-2 text-xs text-slate-600 mt-2">
              <Checkbox checked={f.consent} onCheckedChange={(v) => setF({ ...f, consent: v })} data-testid="enq-consent" className="mt-0.5" />
              {CONSENT}
            </label>
            <DialogFooter>
              <Button className="bg-teal-brand hover:bg-teal-700 text-white w-full" disabled={submitting} onClick={submit} data-testid="enq-submit">
                <Send className="h-4 w-4 mr-2" /> {submitting ? "Submitting…" : "Submit Enquiry"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
